import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createPipeline,
  deletePipeline,
  getPipelineBySlug,
  queryPipelines,
  restorePipeline,
  updatePipeline,
} from '@/features/pipelines/data';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { useProjectFilter } from '@/features/projects';
import {
  gridPaths,
  invalidateGridQuery,
  invalidatePipelineDetail,
  invalidatePipelineDetails,
  invalidatePipelines,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
  useResolvedFeatureFlags,
} from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type {
  PipelineDetailDto,
  PipelineListItemDto,
  PipelineProvider,
  QueryResult,
} from '@flowqa/shared';

type CreatePipelineInput = {
  projectId: string;
  provider: PipelineProvider;
  pipelineKey?: string | null;
  name: string;
  pipelineUrl?: string | null;
  defaultBranch?: string | null;
  tags?: string[];
};

type UpdatePipelineInput = {
  id: string;
  provider?: PipelineProvider;
  pipelineKey?: string | null;
  name?: string | null;
  pipelineUrl?: string | null;
  defaultBranch?: string | null;
  tags?: string[];
};

type PipelineActionInput = { id: string; projectId?: string | null };

export const usePipelinesQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<PipelineListItemDto>, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const isEnabled = Boolean(token) && (options?.enabled ?? true);
  return useQuery<QueryResult<PipelineListItemDto>, ApiError>({
    queryKey: queryKeys.pipelines(effectiveProjectId, orgKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<PipelineListItemDto>> =>
      await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 50,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
        include: { latestBuild: true, metrics: true, metricDeltas: deltasEnabled },
      }),
    staleTime: 30_000,
  });
};

export const usePipelineDetailQuery = (
  slug: string | null,
): UseQueryResult<PipelineDetailDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useQuery<PipelineDetailDto | null, ApiError>({
    queryKey: queryKeys.pipelineDetail(slug, orgKey),
    enabled: Boolean(token && slug),
    queryFn: async (): Promise<PipelineDetailDto | null> => {
      if (!slug) {
        return null;
      }
      return await getPipelineBySlug(slug, selectedOrganisationId ?? undefined);
    },
    staleTime: 15_000,
  });
};

export const useCreatePipeline = (): UseMutationResult<
  PipelineDetailDto,
  ApiError,
  CreatePipelineInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<PipelineDetailDto, ApiError, CreatePipelineInput>({
    mutationFn: async (input) => await createPipeline(input, selectedOrganisationId),
    onSuccess: async (created) => {
      const pipelineSlug = resolvePipelineSlug(created);
      queryClient.setQueryData(queryKeys.pipelineDetail(pipelineSlug, orgKey), created);
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(created.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
      ]);
    },
  });
};

export const useUpdatePipeline = (): UseMutationResult<
  PipelineDetailDto,
  ApiError,
  UpdatePipelineInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<PipelineDetailDto, ApiError, UpdatePipelineInput>({
    mutationFn: async (input) => await updatePipeline(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      const pipelineSlug = resolvePipelineSlug(updated);
      queryClient.setQueryData(queryKeys.pipelineDetail(pipelineSlug, orgKey), updated);
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(updated.projectId, orgKey),
        invalidatePipelineDetail(pipelineSlug, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
      ]);
    },
  });
};

export const useDeletePipeline = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  PipelineActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, PipelineActionInput>({
    mutationFn: async (input) => {
      const result = await deletePipeline({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        ...(deleted.projectId ? [invalidatePipelines(deleted.projectId, orgKey)] : []),
        invalidatePipelineDetails(orgKey),
        invalidateGridQuery(gridPaths.pipelines),
      ]);
    },
  });
};

export const useRestorePipeline = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  PipelineActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, PipelineActionInput>({
    mutationFn: async (input) => {
      const result = await restorePipeline({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (restored) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        ...(restored.projectId ? [invalidatePipelines(restored.projectId, orgKey)] : []),
        invalidatePipelineDetails(orgKey),
        invalidateGridQuery(gridPaths.pipelines),
      ]);
    },
  });
};
